import { jFileLittle } from '../types/file';
import { ApiWrapper, BaseMessages, MaybeWithToken } from '../types/utils';
import { WindowInterface } from '../window/Window';

export interface OF {
    o: string;
    f: string;
}

export type ApiError = { error: number };
export type StandardApiError<T = BaseMessages> = ApiError & { errorMsg: string | undefined; messages: T };
export type AbortApiError = ApiError & { error: -1; aborted: true };

/**
 * deprecated
 */
export class Network {
    private readonly action: string;
    private readonly window: WindowInterface;

    public constructor(window: WindowInterface, action = 'api-front') {
        this.window = window;
        this.action = action;
    }

    public static _formatPostOptions(body: object): RequestInit {
        const source = body ? { body: JSON.stringify(body) } : undefined;
        return Object.assign({ method: 'POST', credentials: 'same-origin' as const }, source);
    }

    public async getUrl(o: string, f: string, urlParams?: Record<string, string>) {
        const backendUrl = await this.window.getBackendUrl();
        let href;

        try {
            const url = new URL(`/${this.action}/${o}/${f}`, backendUrl);
            if (urlParams) {
                Object.entries(urlParams).forEach(([key, value]) => {
                    url.searchParams.set(key, value);
                });
            }
            href = url.href;
        } catch (e) {
            href = `${backendUrl}/${this.action}/${o}/${f}`;
        }
        return href;
    }

    public async getHostname() {
        const splittedBackUrl = (await this.window.getBackendUrl()).split('//');
        return splittedBackUrl.length > 1 ? splittedBackUrl[1] : splittedBackUrl[0];
    }

    public async fetch<T>({ o, f, ...body }: OF, signal?: AbortSignal | null) {
        if (signal === undefined) {
            signal = null;
        }
        const url = await this.getUrl(o, f);
        return this.window.fetch<T>(url, { ...Network._formatPostOptions(body), signal });
    }

    public async get<T, U = BaseMessages>(
        o: string,
        f: string,
        urlParams?: Record<string, string>,
        signal?: AbortSignal | null,
    ) {
        if (signal === undefined) {
            signal = null;
        }

        const url = await this.getUrl(o, f, urlParams);

        return this.window
            .fetch<ApiWrapper<T, U>>(url, {
                method: 'GET',
                credentials: 'same-origin' as const,
                signal: signal,
            })
            .then((res) => {
                return res.json();
            })
            .then((res) => {
                if (res.error === 0) {
                    return res;
                }
                const error: StandardApiError<U> = {
                    error: res.error,
                    errorMsg: res.errorMsg,
                    messages: res.messages,
                };
                throw error;
            })
            .catch((err) => {
                if (signal && signal.aborted) {
                    const abortError: AbortApiError = {
                        error: -1,
                        aborted: true,
                    };
                    throw abortError;
                }
                throw err;
            });
    }

    public post<T, U = BaseMessages>(body: OF, signal?: AbortSignal): Promise<ApiWrapper<T, U>> {
        return this.fetch<ApiWrapper<T, U>>(body, signal)
            .then((res) => res.json())
            .then((res) => {
                if (res.error === 0) {
                    return res;
                }
                const error: StandardApiError<U> = {
                    error: res.error,
                    errorMsg: res.errorMsg,
                    messages: res.messages,
                };
                throw error;
            })
            .catch((err) => {
                if (signal && signal.aborted) {
                    const abortError: AbortApiError = {
                        error: -1,
                        aborted: true,
                    };
                    throw abortError;
                }
                throw err;
            });
    }

    public async postFile(body: FormData) {
        const url = await this.getUrl('file', 'create');

        body.append('format', 'raw-little');

        if (!body.has('token')) {
            const { result } = await this.token();
            body.append('token', result);
        }

        const token = body.get('token');
        if (!token || typeof token !== 'string') {
            return Promise.reject('Missing token');
        }

        return this.window
            .fetch<ApiWrapper<jFileLittle>>(url, {
                method: 'POST',
                credentials: 'same-origin',
                body,
            })
            .then((resp) => resp.json())
            .then((res) => {
                return { response: res, token: token };
            });
    }

    public postCSRF<T, U = BaseMessages>(body: OF, signal?: AbortSignal): Promise<ApiWrapper<T, U>> {
        return this.tokenCSRF().then((tokenCSRF) =>
            this.post(Object.assign(body, { tokenCSRF: tokenCSRF.result }), signal),
        );
    }

    public postToken<T, U = BaseMessages>(body: OF): Promise<ApiWrapper<T, U>> {
        return this.token().then((token) => this.post(Object.assign(body, { token: token.result })));
    }

    public maybePostToken<T, U = BaseMessages, P extends MaybeWithToken<OF> = MaybeWithToken<OF>>(
        body: P,
    ): Promise<ApiWrapper<T, U>> {
        if ('token' in body && body.token) {
            return this.post(body);
        } else {
            return this.postToken(body);
        }
    }

    public token(signal?: AbortSignal): Promise<ApiWrapper<string>> {
        return this.post({ o: 'misc', f: 'token' }, signal);
    }

    public tokenCSRF(): Promise<ApiWrapper<string>> {
        return this.post({ o: 'misc', f: 'tokenCSRF' });
    }
}
